import categories from '@data/cats';
import {
  Banknote,
  CalendarClock,
  CircleDollarSign,
  Globe,
  Mail,
  MapPin,
  Package,
  Tag,
  Ticket,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';
import { Badge, CardTitle, IconTile, Section, SectionTitle } from '@/components/ui';
import { up } from '@/themes';
import { flagForCountryCode } from '@/utils';
import {
  currencyLabel,
  deliveryKeyForBrand,
  formatMoney,
  getNamedProducts,
  getPriceSummary,
  headingKeyForBrand,
  sharedValidityInDays,
  typeKeyForBrand,
} from './brand-content';

// Above this many denominations or options the list is summarised.
const MAX_AMOUNTS = 12;
const MAX_OPTIONS = 12;

// Only categories with a /shop page are linked.
const shopSlugs = new Set(categories.map((category) => category.slug));

const Facts = styled.dl`
  display: grid;
  gap: 12px;
  margin: 32px 0 0;

  ${up.sm} {
    gap: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${up.lg} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const Fact = styled.div`
  align-items: flex-start;
  background-color: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  display: flex;
  gap: 12px;
  min-width: 0;
  padding: 16px;
`;

const FactBody = styled.div`
  min-width: 0;
`;

const Term = styled.dt`
  color: var(--muted-foreground);
  font-size: 14px;
  line-height: 20px;
`;

const Value = styled.dd`
  color: var(--foreground);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  margin: 2px 0 0;
  overflow-wrap: anywhere;

  a {
    color: var(--primary);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  small {
    color: var(--muted-foreground);
    display: block;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
`;

const Badges = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const OptionsTitle = styled(CardTitle)`
  margin-top: 40px;
`;

const Options = styled.ul`
  display: grid;
  gap: 0 32px;
  list-style: none;
  margin: 12px 0 0;
  padding: 0;

  ${up.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Option = styled.li`
  align-items: baseline;
  border-bottom: 1px solid var(--border);
  color: var(--foreground);
  display: flex;
  font-size: 16px;
  gap: 16px;
  justify-content: space-between;
  line-height: 24px;
  padding: 12px 0;

  span:first-child {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  small {
    color: var(--muted-foreground);
    display: block;
    font-size: 14px;
    line-height: 20px;
  }

  strong {
    flex-shrink: 0;
    font-weight: 600;
  }
`;

const More = styled.p`
  color: var(--muted-foreground);
  font-size: 14px;
  line-height: 20px;
  margin: 12px 0 0;
`;

const Item = ({ icon: Icon, label, children }) => (
  <Fact>
    <IconTile $size="sm" aria-hidden="true">
      <Icon size={16} strokeWidth={1.75} />
    </IconTile>
    <FactBody>
      <Term>{label}</Term>
      <Value>{children}</Value>
    </FactBody>
  </Fact>
);

/**
 * "About this gift card": the facts the brand record carries, as a definition
 * list. Below the fold, after the buy box and the issuer's own text. Every row
 * is optional; nothing here is written by hand.
 */
const AboutBrand = ({ brand }) => {
  const t = useTranslations('brand');
  const { locale } = useRouter();
  const { country, products } = brand;
  const prices = getPriceSummary(products);
  const validityDays = sharedValidityInDays(products);
  const namedProducts = getNamedProducts(brand);
  const visibleProducts = namedProducts.slice(0, MAX_OPTIONS);
  const hiddenProductCount = namedProducts.length - visibleProducts.length;
  const brandCategories = brand.categories || [];
  const redemptionMethods = brand.redemptionMethods || [];
  const money = (amountInCents, currency = prices?.currency) =>
    formatMoney(amountInCents, currency, locale);

  let amounts = null;

  if (prices) {
    if (prices.isRange || prices.amounts.length > MAX_AMOUNTS) {
      amounts = t('aboutAmountsRange', {
        low: money(prices.lowInCents),
        high: money(prices.highInCents),
      });
    } else {
      amounts = prices.amounts.map((amount) => money(amount)).join(', ');
    }
  }

  return (
    <Section id="about" $background="var(--muted)" $spacing="compact">
      <SectionTitle>
        {t('aboutTitle', { type: headingKeyForBrand(brand), brandName: brand.name })}
      </SectionTitle>
      <Facts>
        <Item icon={Package} label={t('aboutType')}>
          {t('productKind', { type: typeKeyForBrand(brand) })}
        </Item>
        {amounts && (
          <Item icon={Banknote} label={t('aboutAmounts')}>
            {amounts}
            {prices.increment && (
              <small>
                {t('aboutAmountsIncrement', {
                  increment: money(prices.increment * 100),
                })}
              </small>
            )}
          </Item>
        )}
        {prices && (
          <Item icon={CircleDollarSign} label={t('aboutCurrency')}>
            {currencyLabel(prices.currency, locale)}
          </Item>
        )}
        {products?.length > 0 && (
          <Item icon={Mail} label={t('aboutDelivery')}>
            {t('aboutDeliveryValue', { delivery: deliveryKeyForBrand(brand) })}
          </Item>
        )}
        {redemptionMethods.length > 0 && (
          <Item icon={Ticket} label={t('redemptionMethods')}>
            <Badges>
              {redemptionMethods.map((method) => (
                <Badge key={method} $variant="secondary">
                  {method}
                </Badge>
              ))}
            </Badges>
          </Item>
        )}
        {validityDays && (
          <Item icon={CalendarClock} label={t('aboutValidity')}>
            {t('aboutValidityValue', { days: validityDays })}
          </Item>
        )}
        <Item icon={MapPin} label={t('aboutCountry')}>
          <Link href={`/brands/${country.slug}`} prefetch={false}>
            {country.name}
          </Link>{' '}
          <span aria-hidden="true">{flagForCountryCode(country.id)}</span>
        </Item>
        {brandCategories.length > 0 && (
          <Item icon={Tag} label={t('aboutCategories')}>
            {brandCategories.map((category, index) => (
              <span key={category.id}>
                {index > 0 && ', '}
                {shopSlugs.has(category.slug) ? (
                  <Link href={`/shop/${category.slug}`} prefetch={false}>
                    {category.name}
                  </Link>
                ) : (
                  category.name
                )}
              </span>
            ))}
          </Item>
        )}
        {!!brand.websiteUrl && (
          <Item icon={Globe} label={t('website')}>
            <a href={brand.websiteUrl} rel="noopener nofollow" target="_blank">
              {brand.websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          </Item>
        )}
      </Facts>
      {visibleProducts.length > 0 && (
        <>
          <OptionsTitle>{t('aboutOptions')}</OptionsTitle>
          <Options>
            {visibleProducts.map((product) => (
              <Option key={product.id}>
                <span>
                  {product.name}
                  {product.validityDays && (
                    <small>{t('validFor', { days: product.validityDays })}</small>
                  )}
                </span>
                {product.amountInCents && (
                  <strong>{money(product.amountInCents, product.currency)}</strong>
                )}
              </Option>
            ))}
          </Options>
          {hiddenProductCount > 0 && (
            <More>{t('aboutMoreOptions', { count: hiddenProductCount })}</More>
          )}
        </>
      )}
    </Section>
  );
};

export default AboutBrand;
